import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Check, X, User, Search, ShieldAlert, Gavel, ArrowRight } from "lucide-react";
import { DonkeyAvatar } from "@/components/DonkeyMascot";
import { VerdictBadge, ScoreRing } from "@/components/DonkeyBits";
import { useCountUp } from "@/hooks/useCountUp";
import { ENGINE_IDEAS, ENGINE_PHASES, buildScriptedIdea } from "./homeData";

const PHASE_MS = 2600;
const PHASE_ICONS = [User, Search, ShieldAlert, Gavel];

function PhaseBar({ phase }) {
  return (
    <div className="flex gap-1.5" data-testid="engine-phasebar">
      {ENGINE_PHASES.map((p, i) => (
        <div key={p.key} className="flex-1 h-1.5 rounded-full bg-donkey-line/70 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-donkey-orange to-donkey-deeporange"
            initial={false}
            animate={{ width: i < phase ? "100%" : i === phase ? "100%" : "0%" }}
            transition={{ duration: i === phase ? PHASE_MS / 1000 : 0.3, ease: "easeInOut" }}
          />
        </div>
      ))}
    </div>
  );
}

function Understand({ idea }) {
  return (
    <motion.div key="u" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <p className="text-xs text-donkey-grey mb-3">Reading what you bring to the table</p>
      <div className="flex flex-wrap gap-2">
        {idea.chips.map((c, i) => (
          <motion.span
            key={c + i}
            initial={{ opacity: 0, y: 6, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: i * 0.16 }}
            className="px-3 py-1.5 rounded-full text-xs bg-donkey-orange/10 border border-donkey-orange/30 text-donkey-warmorange"
          >
            {c}
          </motion.span>
        ))}
      </div>
    </motion.div>
  );
}

function Research({ idea, active, reduce }) {
  const sources = useCountUp(idea.sources, active, { duration: 1600, reduce });
  return (
    <motion.div key="r" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
      {idea.research.map((r, i) => (
        <div key={r.label}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-donkey-warm/80">{r.label}</span>
            <span className="font-mono text-donkey-warmorange">{r.value}</span>
          </div>
          <div className="h-1.5 rounded-full bg-donkey-line/70 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-donkey-orange"
              initial={{ width: 0 }}
              animate={{ width: `${r.value}%` }}
              transition={{ duration: 1, delay: 0.15 + i * 0.18, ease: "easeOut" }}
            />
          </div>
        </div>
      ))}
      <p className="text-[11px] text-donkey-grey pt-1">
        <span className="font-mono text-donkey-warm">{sources}</span> sources reviewed
      </p>
    </motion.div>
  );
}

function Challenge({ idea }) {
  return (
    <motion.div key="c" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2.5">
      {idea.checks.map((c, i) => (
        <motion.div
          key={c.t}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.28 }}
          className="flex items-center gap-2.5 text-sm"
        >
          {c.ok ? (
            <span className="w-5 h-5 rounded-full bg-donkey-green/15 border border-donkey-green/40 flex items-center justify-center shrink-0">
              <Check className="w-3 h-3 text-donkey-green" />
            </span>
          ) : (
            <span className="w-5 h-5 rounded-full bg-donkey-red/15 border border-donkey-red/40 flex items-center justify-center shrink-0">
              <X className="w-3 h-3 text-donkey-red" />
            </span>
          )}
          <span className={c.ok ? "text-donkey-warm/90" : "text-donkey-grey line-through"}>{c.t}</span>
        </motion.div>
      ))}
    </motion.div>
  );
}

function Verdict({ idea, active, reduce }) {
  const score = useCountUp(idea.verdict.score, active, { duration: 1100, reduce });
  return (
    <motion.div key="v" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-4">
      <ScoreRing value={score} size={82} />
      <div className="min-w-0">
        <VerdictBadge label={idea.verdict.label} />
        <p className="text-sm text-donkey-warm/80 mt-2 leading-snug">{idea.verdict.note}</p>
      </div>
    </motion.div>
  );
}

function ChallengeInput({ onSubmit }) {
  const [v, setV] = useState("");
  const chips = ["A dog-walking round", "Notion templates", "Local coffee cart"];
  return (
    <div className="mt-5 pt-4 border-t border-donkey-line/70">
      <p className="text-[11px] uppercase tracking-wider text-donkey-grey mb-2">Challenge my idea &mdash; demonstration</p>
      <div className="flex flex-wrap gap-2 mb-2">
        {chips.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onSubmit(c)}
            data-testid={`challenge-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            className="text-xs px-2.5 py-1 rounded-full border border-donkey-line text-donkey-grey hover:border-donkey-orange hover:text-donkey-warm transition-colors"
          >
            {c}
          </button>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(v);
          setV("");
        }}
        className="flex gap-2"
      >
        <input
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder="Type any idea…"
          aria-label="Type any idea to challenge (demonstration)"
          data-testid="challenge-input"
          className="flex-1 min-w-0 bg-donkey-deep border border-donkey-line rounded-full px-4 h-10 text-sm text-donkey-warm placeholder:text-donkey-grey focus:outline-none focus:border-donkey-orange transition-colors"
        />
        <button
          type="submit"
          data-testid="challenge-submit"
          className="shrink-0 h-10 px-4 rounded-full bg-donkey-orange text-donkey-black font-semibold text-sm hover:bg-donkey-deeporange transition-colors"
        >
          Challenge it
        </button>
      </form>
      <p className="mt-2 text-[10px] text-donkey-grey/70 leading-snug">
        Scripted illustration. The Donkey doesn't analyse your real answer here &mdash; start Prove for that.
      </p>
    </div>
  );
}

export function HeroIntelligenceEngine() {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const [ideaIndex, setIdeaIndex] = useState(0);
  const [phase, setPhase] = useState(0);
  const [custom, setCustom] = useState(null);
  const idea = custom || ENGINE_IDEAS[ideaIndex];

  useEffect(() => {
    if (reduce) {
      setPhase(3);
      return;
    }
    // Pause on the visitor's own challenge verdict so the payoff + nudge stay on screen.
    if (phase === 3 && custom) return;
    const dur = phase === 3 ? PHASE_MS + 1100 : PHASE_MS;
    const id = setTimeout(() => {
      if (phase < 3) {
        setPhase(phase + 1);
        return;
      }
      setIdeaIndex((i) => (i + 1) % ENGINE_IDEAS.length);
      setPhase(0);
    }, dur);
    return () => clearTimeout(id);
  }, [phase, custom, reduce]);

  const submitChallenge = (text) => {
    const t = (text || "").trim();
    if (!t) return;
    setCustom(buildScriptedIdea(t));
    setPhase(0);
  };

  const tryAnother = () => {
    setCustom(null);
    setIdeaIndex((i) => (i + 1) % ENGINE_IDEAS.length);
    setPhase(0);
  };

  const PhaseIcon = PHASE_ICONS[phase];

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.dispatchEvent(new CustomEvent("donkey-engine-phase", { detail: { phase, custom: !!custom } }));
    } catch (e) { /* noop */ }
    if (phase === 3) {
      try { window.dispatchEvent(new CustomEvent("donkey-verdict")); } catch (e) { /* noop */ }
    }
  }, [phase, ideaIndex, custom]);

  return (
    <div
      className="relative rounded-3xl border border-donkey-orange/40 bg-donkey-bg/95 backdrop-blur-xl p-5 sm:p-6 shadow-[0_24px_90px_rgba(0,0,0,0.6),0_0_60px_rgba(249,115,22,0.12)] ring-1 ring-white/5"
      data-testid="hero-engine"
    >
      <div className="flex items-center gap-3 mb-4">
        <DonkeyAvatar size={40} />
        <div className="min-w-0">
          <p className="font-heading font-semibold text-donkey-warm text-sm leading-tight">Honest Donkey Intelligence Engine</p>
          <p className="text-[11px] text-donkey-grey flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-donkey-green animate-pulse-soft" />
            <PhaseIcon className="w-3 h-3 text-donkey-orange" />
            {ENGINE_PHASES[phase].label}
          </p>
        </div>
        <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border border-donkey-line text-donkey-grey shrink-0">
          Demo
        </span>
      </div>

      <p className="text-[11px] uppercase tracking-wider text-donkey-grey mb-1 flex items-center gap-2">
        Evaluating
        <span className="normal-case tracking-normal text-donkey-warmorange/90 font-medium" data-testid="engine-persona">
          {idea.persona}
        </span>
      </p>
      <p className="font-heading font-semibold text-donkey-warm mb-4 truncate" data-testid="engine-idea-title">
        {idea.title}
      </p>

      <PhaseBar phase={phase} />

      <div className="mt-4 min-h-[176px]">
        <AnimatePresence mode="wait">
          {phase === 0 && <Understand idea={idea} />}
          {phase === 1 && <Research idea={idea} active={phase === 1} reduce={reduce} />}
          {phase === 2 && <Challenge idea={idea} />}
          {phase === 3 && <Verdict idea={idea} active={phase === 3} reduce={reduce} />}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {custom && phase === 3 && (
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: 8 }}
            transition={{ duration: 0.5, delay: reduce ? 0 : 0.35, ease: "easeOut" }}
            className="mt-4 rounded-2xl border border-donkey-orange/30 bg-gradient-to-br from-donkey-orange/12 to-transparent p-4"
            data-testid="challenge-conversion-nudge"
          >
            <p className="text-sm font-heading font-semibold text-donkey-warm">That was only the demonstration.</p>
            <p className="text-sm text-donkey-warm/80 mt-0.5">Ready to put your real idea through the Donkey?</p>
            <button
              type="button"
              onClick={() => navigate("/pricing?buy=prove")}
              data-testid="challenge-analyse-real-btn"
              className="mt-3 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-donkey-orange text-donkey-black font-bold text-sm tracking-wide hover:bg-donkey-deeporange transition-all shadow-[0_4px_20px_rgba(249,115,22,0.3)] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-donkey-orange focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-black"
            >
              ANALYSE MY REAL IDEA <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-donkey-grey mt-2 leading-snug">
              Get the full interview, research, opportunity analysis and evidence-based verdict.
            </p>
            <button
              type="button"
              onClick={tryAnother}
              data-testid="challenge-try-another"
              className="mt-2 text-xs text-donkey-grey hover:text-donkey-warmorange underline underline-offset-2 decoration-donkey-line hover:decoration-donkey-orange transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-donkey-orange focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-black rounded"
            >
              Try another idea
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <ChallengeInput onSubmit={submitChallenge} />
    </div>
  );
}

export default HeroIntelligenceEngine;
